import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefCallback,
} from 'react';

import { isAngledEffectiveMode } from '../shared/chart-tokens';
import type {
  ChartCategoryLabelMode,
  EffectiveCategoryLabelMode,
} from '../shared/chart-tokens';

import {
  buildAutoModeCacheKey,
  getResolvedAutoLabelMode,
  setResolvedAutoLabelMode,
} from './bar-chart-layout-cache';
import { resolveMeasuredOverflow } from './bar-chart-axis-margins';
import {
  countTruncatedCategoryLabels,
  getTruncationSeverity,
  hasTruncationProbeLabels,
  resolveAutoLabelMode,
} from './bar-chart-auto-label-mode';
import {
  scheduleRetryingMeasure,
  useResizeMeasureSignal,
} from './use-bar-chart-measurement-scheduler';

interface UseAutoCategoryLabelModeOptions {
  categoryLabelMode: ChartCategoryLabelMode;
  categories: readonly string[];
  isValid: boolean;
  cacheId?: string;
}

interface UseAutoCategoryLabelModeResult {
  chartRootRef: RefCallback<HTMLDivElement>;
  chartRootElement: HTMLDivElement | null;
  effectiveLabelMode: EffectiveCategoryLabelMode;
}

/** Warm-starts auto mode from cache; fixed modes pass through unchanged. */
const resolveInitialEffectiveMode = (
  categoryLabelMode: ChartCategoryLabelMode,
  cacheId: string | undefined,
  categoriesKey: string
): EffectiveCategoryLabelMode => {
  if (categoryLabelMode !== 'auto') {
    return categoryLabelMode;
  }

  if (!cacheId || !categoriesKey) {
    return 'truncate';
  }

  return (
    getResolvedAutoLabelMode(buildAutoModeCacheKey(cacheId, categoriesKey)) ??
    'truncate'
  );
};

/**
 * Resolves the concrete x-axis label mode for `categoryLabelMode="auto"`.
 *
 * Lifecycle:
 * 1. Warm-start from the last resolved mode cache so remounts do not flash truncate.
 * 2. Track the mounted chart root with a callback ref and observe resize events.
 * 3. Measure truncate probes after Recharts renders ticks (visible in truncate mode,
 *    hidden horizontal probes while angled).
 * 4. Resolve against visible angled overflow and probe truncation without swapping
 *    the chart into a temporary reprobe mode.
 */
export const useAutoCategoryLabelMode = ({
  categoryLabelMode,
  categories,
  isValid,
  cacheId,
}: UseAutoCategoryLabelModeOptions): UseAutoCategoryLabelModeResult => {
  const chartRootRef = useRef<HTMLDivElement | null>(null);
  const [chartRootElement, setChartRootElement] =
    useState<HTMLDivElement | null>(null);
  const categoriesKey = useMemo(() => categories.join('\u0000'), [categories]);
  const initialEffectiveMode = resolveInitialEffectiveMode(
    categoryLabelMode,
    cacheId,
    categoriesKey
  );
  const [effectiveLabelMode, setEffectiveLabelMode] =
    useState<EffectiveCategoryLabelMode>(initialEffectiveMode);
  const handleChartRootRef = useCallback((element: HTMLDivElement | null) => {
    chartRootRef.current = element;
    setChartRootElement(element);
  }, []);
  const measureRequestId = useResizeMeasureSignal({
    element: chartRootElement,
    enabled: categoryLabelMode === 'auto',
  });

  useEffect(() => {
    if (categoryLabelMode === 'auto') {
      const warmStartMode = resolveInitialEffectiveMode(
        categoryLabelMode,
        cacheId,
        categoriesKey
      );

      setEffectiveLabelMode(warmStartMode);

      return;
    }

    setEffectiveLabelMode(categoryLabelMode);
  }, [categoryLabelMode, categoriesKey, cacheId]);

  useLayoutEffect(() => {
    const root = chartRootElement;

    if (!root || !isValid || categoryLabelMode !== 'auto') {
      return;
    }

    return scheduleRetryingMeasure({
      measure: () => {
        const measuredOverflow = isAngledEffectiveMode(effectiveLabelMode)
          ? resolveMeasuredOverflow(root)
          : null;
        const isReady =
          hasTruncationProbeLabels(root) &&
          (!isAngledEffectiveMode(effectiveLabelMode) ||
            measuredOverflow !== null);

        if (!isReady) {
          return false;
        }

        const leftOverflowPx = measuredOverflow?.left ?? 0;
        const truncatedCount = countTruncatedCategoryLabels(root);
        const maxOverflowRatio = getTruncationSeverity(root);
        const nextMode = resolveAutoLabelMode({
          currentMode: effectiveLabelMode,
          truncatedCount,
          leftOverflowPx,
          maxOverflowRatio,
        });

        if (cacheId && categoriesKey) {
          setResolvedAutoLabelMode(
            buildAutoModeCacheKey(cacheId, categoriesKey),
            nextMode
          );
        }

        if (nextMode !== effectiveLabelMode) {
          setEffectiveLabelMode(nextMode);
        }

        return true;
      },
    });
  }, [
    cacheId,
    categoriesKey,
    categoryLabelMode,
    chartRootElement,
    effectiveLabelMode,
    isValid,
    measureRequestId,
  ]);

  return {
    chartRootRef: handleChartRootRef,
    chartRootElement,
    effectiveLabelMode,
  };
};
